import { Autocomplete, Button, Input, Modal, Spinner } from "src/components";
import InvoiceLayout from "src/pages/invoice-master/invoice-layout.component";

import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { getCompanyList, getSignedUrlPO, reconcilePoFile, uploadFileToPresignedUrl, getReconciliationRecords } from "src/services/po-api";
import { useToast } from "src/hooks/useToast";
import { downloadReconciliationFormat } from "src/utils/fileDownloadUtils";
import Pagination from "src/components/ui/Pagination";
import { DatePickerWithRange } from "src/components/ui/date-range.component";
import { DateRange } from "react-day-picker";
import moment from "moment";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import { getCounterTypes } from "src/services/common-api";
import { getReconciliationModuleTypes } from "src/services/po-api";
import { RECONCILIATION_SO_TYPE_OPTIONS } from "../PurchaseOrder/po.constants";

const PAGE_SIZE = 10;

const ReconciliationHomeScreen = () => {
  const dispatch = useDispatch();
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [companyData, setCompanyData] = useState<any>([]);
  const [soType, setSoType] = useState<any>(); //M10-11928
  const [moduleData, setModuleData] = useState<any>([]);
  // module -> allowed SO Types, from OMS module_order_type_hdr. Source of truth for which
  // modules are enabled and which types each allows.
  const [moduleTypeConfig, setModuleTypeConfig] = useState<any[]>([]);
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [reconciliationResults, setReconciliationResults] = useState<any>(null);
  
  // New state for reconciliation records list
  const [reconciliationRecords, setReconciliationRecords] = useState<any>([]);
  const [isFetchingRecords, setIsFetchingRecords] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const { toast } = useToast();

  const isExcelFile = (f: File | null) => {
    if (!f) return false;
    const name = f.name?.toLowerCase?.() || "";
    const type = f.type || "";
    const byExt = name.endsWith(".xlsx") || name.endsWith(".xls");
    const byMime =
      type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      type === "application/vnd.ms-excel";
    return byExt || byMime;
  };

  const companyHandler = (value: any) => {
    setSelectedCompany(value);
  };
  //M10-11928
  const soTypeHandler = (value: any) => {
    setSoType(value);
  };
  //M10-11928

  // An empty config (first load, or a failed config fetch) is treated as "unknown" rather than
  // "nothing allowed", so a transient API failure cannot lock the whole screen — the
  // reconciliation lambda re-checks the same table server-side regardless.
  const hasModuleTypeConfig = moduleTypeConfig.length > 0;

  const isModuleEnabled = (moduleCode: any) =>
    !hasModuleTypeConfig ||
    moduleTypeConfig.some((cfg: any) => Number(cfg?.module_code) === Number(moduleCode));

  const soTypeOptions =
    moduleTypeConfig.find((cfg: any) => Number(cfg?.module_code) === Number(selectedModule?.module_code))
      ?.so_types || (hasModuleTypeConfig ? [] : RECONCILIATION_SO_TYPE_OPTIONS);

  // Clear the SO Type on a module switch so a KAM-only SAO-CN selection cannot survive a switch
  // back to Medvol and get rejected by the lambda.
  const moduleHandler = (value: any) => {
    setSelectedModule(value);
    setSoType(undefined);
  };

  const fetchReconciliationRecords = async () => {
    if (!selectedCompany) {
      toast({ description: "Please select a company to view records", variant: "destructive" });
      return;
    }
    
    try {
      setIsFetchingRecords(true);
      const params: any = { 
        company_code: selectedCompany?.code, 
        module_code: selectedModule?.module_code,
        page: pageNo, 
        limit: PAGE_SIZE 
      };

      // Add date filtering if date range is selected
      if (dateRange?.from && dateRange?.to) {
        params.date_from = moment(dateRange.from).utcOffset("+05:30").format("YYYY-MM-DDTHH:mm:ss");
        params.date_to = moment(dateRange.to).utcOffset("+05:30").format("YYYY-MM-DDTHH:mm:ss");
      }
      

      const res = await getReconciliationRecords(params);
      setReconciliationRecords(res?.data?.data || []);
      setTotalPages(Math.ceil(res?.data?.total / PAGE_SIZE) || 1);
      setTotal(res?.data?.total || 0);
      toast({ description: "Reconciliation records fetched successfully", variant: "default" });
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message || "Failed to fetch reconciliation records", variant: "destructive" });
    } finally {
      setIsFetchingRecords(false);
    }
  };

  const handleSubmitFile = async () => {
    if (!selectedCompany) {
      toast({ description: "Please select company", variant: "destructive" });
      return;
    }
    if (!selectedModule) {
      toast({ description: "Please select module", variant: "destructive" });
      return;
    }
    //M10-11928
    if (!soType) {
      toast({ description: "Please select SO Type", variant: "destructive" });
      return;
    }
    //M10-11928
    if (!file) {
      toast({ description: "Please select file", variant: "destructive" });
      return;
    }
    if (!isExcelFile(file)) {
      toast({ description: "Only Excel files (.xls, .xlsx) are allowed", variant: "destructive" });
      return;
    }

    setIsUploadingFile(true);

    try {
      const presignedRes = await getSignedUrlPO({ fileName: file?.name, company_code: selectedCompany?.code });
      const presigned = presignedRes?.data?.data?.presignedUrls?.[file?.name];
      if (!presigned?.uploadUrl || !presigned?.s3Key) {
        throw new Error("Failed to get presigned URL");
      }

      await uploadFileToPresignedUrl(file, presigned.uploadUrl, selectedCompany?.code);

      const payload = {
        company_code: selectedCompany?.code,
        module_code: selectedModule?.module_code ?? 1,
        soType: soType?.code,  //M10-11928
        fileKey: presigned.s3Key,
      };

      const response = await reconcilePoFile(payload);
      setReconciliationResults(response?.data);
      setShowResultsModal(true);
      toast({ description: "File processed successfully", variant: "default" });
      setFile(null);
    } catch (error: any) {
      toast({ description: error?.response?.data?.message || "Something went wrong", variant: "destructive" });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleDownloadFormat = async () => {
    try {
      await downloadReconciliationFormat();
      toast({ description: "Format file downloaded successfully", variant: "default" });
    } catch (error: any) {
      toast({ description: error.message || "Failed to download format file", variant: "destructive" });
    }
  };

  const handleDownloadErrorFile = () => {
    if (reconciliationResults?.error_file?.url) {
      const link = document.createElement('a');
      link.href = reconciliationResults.error_file.url;
      link.download = reconciliationResults.error_file.s3_key.split('/').pop() || 'error_file.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ description: "Error file downloaded successfully", variant: "default" });
    }
  };

  useEffect(() => {
    if (!companyData?.length) {
      getCompanyList()
        .then((res) => setCompanyData(res?.data))
        .catch((err) => console.log(err));
    }
  }, [dispatch]);

  useEffect(() => {
    getReconciliationModuleTypes()
      .then((res) => setModuleTypeConfig(res?.data?.data || []))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (!moduleData?.length) {
      // PUC=true unlocks the full module list (Medvol/POB/PUC/KAM); default returns only 1,2
      getCounterTypes("?PUC=true")
        .then((res) => {
          const moduleList = res?.data?.data || [];
          setModuleData(moduleList);
          // Seed the default selection to module_code 1 (Medvol)
          setSelectedModule(moduleList.find((item: any) => item?.module_code === 1) || null);
        })
        .catch((err) => console.log(err));
    }
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchReconciliationRecords();
    }
  }, [pageNo, selectedCompany, selectedModule, dateRange]);

  return (
    <InvoiceLayout>
      <header className="flex flex-col justify-between py-6 mx-auto max-h-[100vh] shadow-md">
        <div className="flex justify-between px-[3.75rem]">
          <div className="">
            <h1 className="text-[#49484C] font-bold text-[2rem]">Reconciliation</h1>
          </div>
        </div>
      </header>

      <div className="mt-10 px-[5.75rem] p-6">
        <div className="flex justify-between items-center gap-2">
          <div className="flex gap-2 w-[70%] [@media(min-width:1580px)]:w-[50%]">
            <Autocomplete
              onSelectionChange={companyHandler}
              placeholder="Company"
              inputClassName="w-fit"
              suggestionList={companyData || []}
              displayKey="name"
              defaultValue={selectedCompany}
              uniqueKey="code"
            />
            <Autocomplete
              onSelectionChange={moduleHandler}
              placeholder="Module"
              inputClassName="w-fit"
              suggestionList={moduleData || []}
              displayKey="module_desc"
              defaultValue={selectedModule}
              uniqueKey="module_code"
              // A module with no rows in module_order_type_hdr is not enabled for reconciliation
              // (POB & PUC today) — still listed, but unselectable.
              isOptionDisabled={(item: any) => !isModuleEnabled(item?.module_code)} />
            {/* M10-11928 */}
            <Autocomplete
              onSelectionChange={soTypeHandler}
              placeholder="SO Type"
              inputClassName="w-fit"
              // Types allowed under the selected module, straight from the config table — SAO-CN
              // shows up only because KAM has a row for it, not because of any client-side rule.
              suggestionList={soTypeOptions}
              displayKey="name"
              defaultValue={soType}
              uniqueKey="code" />
              {/* M10-11928 */}
            <Input
              placeholder="File"
              type="file"
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const selected = e.target.files?.[0] || null;
                if (selected && !isExcelFile(selected)) {
                  toast({ description: "Only Excel files (.xls, .xlsx) are allowed", variant: "destructive" });
                  e.currentTarget.value = "";
                  setFile(null);
                  return;
                }
                setFile(selected);
              }}
            />
            <Button
              isLoading={isUploadingFile}
              loaderSize="small"
              className="bg-[#8051FF] text-white hover:bg-[#6640d2] min-w-[100px]"
              onClick={handleSubmitFile}
              disabled={isUploadingFile}
              module={fineGrainedPermissionsKeys.Reconciliation.SUBMITRECONCILIATION.module}
              action={fineGrainedPermissionsKeys.Reconciliation.SUBMITRECONCILIATION.action}
            >
              Submit
            </Button>
            <Button
              onClick={fetchReconciliationRecords}
              isLoading={isFetchingRecords}
              loaderSize="small"
              className="font-normal text-black bg-[#F1F1F1]"
              module={fineGrainedPermissionsKeys.Reconciliation.VIEWRECONCILIATIONDETAILS.module}
              action={fineGrainedPermissionsKeys.Reconciliation.VIEWRECONCILIATIONDETAILS.action}
            >
              Refresh
            </Button>
          </div>
          <div className="flex gap-2">
            <Button className="bg-[#28a745] text-white hover:bg-[#218838] min-w-[150px] flex items-center gap-2" onClick={handleDownloadFormat}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Format
            </Button>
          </div>
        </div>

        {/* Reconciliation Records Section */}
        <div className="mt-8">
          <div className="flex flex-col justify-between gap-2">
            <div className="flex justify-between items-center gap-2">
              <div className="flex gap-2 items-center">
                {/* <Button
                  onClick={fetchReconciliationRecords}
                  isLoading={isFetchingRecords}
                  loaderSize="small"
                  className="font-normal text-black bg-[#F1F1F1]">
                  Refresh
                </Button> */}
              </div>
              <div className="flex gap-0 items-center border border-gray-200 rounded-lg p-2">
                <DatePickerWithRange
                  date={dateRange}
                  dateRangeHandler={setDateRange}
                  className="w-fit"
                />
                {dateRange && (
                  <Button
                    onClick={() => setDateRange(undefined)}
                    className="font-normal text-red-600 bg-red-50 hover:bg-red-100 border border-red-200">
                    Clear
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-white rounded-2xl mt-4 shadow p-8 w-[100%] border">
              <h2 className="font-bold text-lg text-[#49484C] mb-4">Reconciliation Records</h2>
              {isFetchingRecords ? <div className="flex items-center justify-center h-[20rem]"><Spinner /></div> : (reconciliationRecords?.length ? (
                <>
                  <table className="min-w-full mt-5 border-2 border-gray-200">
                    <thead className="w-full bg-gray-100">
                      <tr>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Reconciliation ID</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Company Code</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Module</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Created Date</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Updated Date</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Status</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Item Count</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Processed Count</th> {/*M10-11928 */}
                        {/* <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Created By</th>
                        <th className="px-2 py-2 text-sm text-left text-gray-600 border-l">Updated By</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {reconciliationRecords?.map((item: any, index: number) => (
                        <tr key={index} className="text-sm text-[#303030]">
                          <td className="px-2 py-2 border-t border-l">{item?.reconciliation_header_id}</td>
                          <td className="px-2 py-2 border-t border-l">{item?.company_code}</td>
                          <td className="px-2 py-2 border-t border-l">
                            {moduleData?.find((m: any) => m?.module_code === item?.module_code)?.module_desc ??
                              item?.module_code}
                          </td>
                          <td className="px-2 py-2 border-t border-l">{moment(item?.created_date).format("MMMM Do YYYY") + " " + moment(item?.created_date).format("h:mm a")}</td>
                          <td className="px-2 py-2 border-t border-l">{moment(item?.updated_date).format("MMMM Do YYYY") + " " + moment(item?.updated_date).format("h:mm a")}</td>
                          <td className="px-2 py-2 border-t border-l">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${item?.reconciliation_process_status?.toLowerCase() === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : item?.reconciliation_process_status?.toLowerCase() === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                            >
                              {item?.reconciliation_process_status}
                            </span>
                          </td>
                          <td className="px-2 py-2 border-t border-l">{item?.item_count}</td>
                          <td className="px-2 py-2 border-t border-l">{item?.processed_count}</td>  {/*M10-11928 */}
                          {/* <td className="px-2 py-2 border-t border-l">{item?.created_by}</td>
                          <td className="px-2 py-2 border-t border-l">{item?.updated_by}</td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-[#96969B]">
                  <span className="material-icons text-4xl mb-2">info</span>
                  No reconciliation records found. Please select a company and try again.
                </div>
              ))}
            </div>
            {selectedCompany && (
              <Pagination
                currentPage={pageNo}
                totalPages={totalPages}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={(page) => setPageNo(page)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Reconciliation Results Modal */}
      <Modal 
        isOpen={showResultsModal} 
        isHeader={true} 
        onCloseModal={setShowResultsModal}
        title="Reconciliation Results"
        backdropClose={true}
      >
        <div className="p-6 w-[500px]">
          {reconciliationResults && (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  {reconciliationResults.message}
                </h2>
                <p className="text-sm text-gray-600">
                  Reconciliation Header ID: {reconciliationResults.reconciliation_header_id}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 my-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {reconciliationResults.passed_rows}
                  </div>
                  <div className="text-sm text-green-700 font-medium">Passed Rows</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {reconciliationResults.error_rows}
                  </div>
                  <div className="text-sm text-red-700 font-medium">Error Rows</div>
                </div>
              </div>

              {reconciliationResults.error_file && reconciliationResults.error_rows > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-yellow-800 mb-1">Error File Available</h3>
                      <p className="text-sm text-yellow-700">
                        Download the error file to see details of {reconciliationResults.error_rows} failed rows
                      </p>
                    </div>
                    <Button
                      onClick={handleDownloadErrorFile}
                      className="bg-yellow-600 text-white hover:bg-yellow-700 px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Download
                    </Button>
                  </div>
                </div>
              )}

              {reconciliationResults.passed_rows > 0 && reconciliationResults.error_rows === 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-green-800 font-medium">
                    🎉 All rows processed successfully!
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end mt-6">
            <Button
              onClick={() => setShowResultsModal(false)}
              className="bg-[#8051FF] text-white hover:bg-[#6E3DFF] px-6 py-2 rounded-lg"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </InvoiceLayout>
  );
};

export default ReconciliationHomeScreen;
